/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Typography, Modal, List, message } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { queryCache, useMutation, useQuery } from 'react-query';

// local imports
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { useProjectId } from '~src/hooks/useProjectId';
import { deleteCommit, loadCommits } from '~src/services/api';
import { parseMessagePreview } from '~src/utils/messagePreview';
import IconButton from '~components/_common/IconButton/IconButton';
import { useFolders } from '~src/hooks/api-hooks/useFolders';
import { DB_STATE } from '~src/types/enums';
import { DbCommit } from '~src/types/db';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';

export const CommitsModal: React.FC = () => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const projectId = useProjectId();
  const foldersQuery = useFolders(
    {
      as_admin:
        window.sessionStorage.getItem('appState') === 'superUser'
          ? true
          : undefined,
    },
    { enabled: false }
  );
  const commitsKey = [
    'commits',
    { projectId: String(projectId), state: DB_STATE.DRAFT },
  ];
  const didShowRef = useRef(false);
  const commitsQuery = useQuery(commitsKey, loadCommits, {
    // load after we got folders
    enabled: !didShowRef.current && projectId && foldersQuery.data,
  });
  const [doDeleteCommit] = useMutation(deleteCommit, {
    onError: () => {
      message.error(intl.formatMessage({ id: 'commit-delete-failed' }));
    },
    onSuccess: (removedCommit) => {
      message.success(intl.formatMessage({ id: 'commit-delete-success' }));
      queryCache.setQueryData<DbCommit[]>(commitsKey, (commits = []) =>
        commits.filter((v) => v.id !== removedCommit.id)
      );
    },
  });
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!commitsQuery.data) return;

    const hasData = commitsQuery.data.length > 0;
    // will show on first load, and hide when there's no data
    setTimeout(() => setVisible(hasData), 500); // timeout to avoid flicker when going from 1 to 0 cout

    if (!didShowRef.current) {
      didShowRef.current = true;
    }
  }, [commitsQuery.data]);

  return (
    <Modal
      title={
        <ModalTitle
          text={intl.formatMessage({ id: 'unfinished-commits' })}
          icon={['fas', 'exclamation-circle']}
          iconButtonProps={{ color: 'red' }}
        />
      }
      centered
      width={450}
      footer={null}
      visible={visible}
      onCancel={() => setVisible(false)}
      maskClosable={false}>
      <Typography.Text>
        {intl.formatMessage({ id: 'unfinished-commits-helper' })}
      </Typography.Text>

      <List
        bordered
        css={styles.list}
        dataSource={commitsQuery.data}
        renderItem={(item) => (
          <List.Item
            key={item.id}
            css={styles.listItem}
            extra={[
              <div key={item.id + '-actions'}>
                {item.is_important && (
                  <IconButton
                    icon={['far', 'star']}
                    backgroundOff
                    tooltip={intl.formatMessage({
                      id: 'important-tooltip',
                    })}
                  />
                )}

                <IconButton
                  icon={['far', 'trash']}
                  color="red"
                  containerStyle={{ marginRight: -10 }}
                  click={() =>
                    doDeleteCommit({
                      commit: item,
                      projectId: String(projectId),
                    })
                  }
                />
              </div>,
            ]}>
            <Typography.Text strong>
              {parseMessagePreview(item.description, 100)}
            </Typography.Text>
          </List.Item>
        )}
      />
    </Modal>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  list: {
    maxHeight: 307,
    overflowY: 'auto',
    margin: '14px 0',
  },
  listItem: {
    ':hover': {
      background: 'rgb(250, 250, 250)',
    },
  },
});
